import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import {
  templateRelaySitesApi,
  mainTemplatesApi,
  relaySitesApi,
  protocolsApi,
  ApiError,
  type MainTemplate,
  type MainProtocol,
  type RelaySite,
  type TemplateRelaySite,
} from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TableShell, type ColumnSpec } from "@/components/TableShell";
import { InheritedModelConfigEditor } from "@/components/InheritedModelConfigEditor";
import { RelayBindingEditModal } from "@/components/RelayBindingEditModal";
import { Modal } from "@/components/ui/modal";
import { modelSourceLabel } from "@/lib/modelConfig";

// ---------------------------------------------------------------------------
// /relay-bindings — 中转站绑定(v6→main 域重构)
//
// main_template × relay_site 多对多绑定。主键 = (template_id, site_id)。
// 一个站点可绑多个模板;一个模板可绑多个站点。绑定承载 groups + priority,
// 分发派发时按「选中站点 × 绑定模板」展开,并按优先级(MAX)选路。
//
// 列表跨模板汇总自 GET /template-relay-sites;增删改用嵌套
// /main-templates/:id/sites。
// ---------------------------------------------------------------------------

const inpCls = "h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring";

export function MainBindingsPage() {
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["relay-bindings"],
    queryFn: () => templateRelaySitesApi.listAll(),
    staleTime: 15_000,
  });
  const bindings: TemplateRelaySite[] = q.data?.items ?? [];

  const templatesQ = useQuery({ queryKey: ["main-templates"], queryFn: () => mainTemplatesApi.list() });
  const templates: MainTemplate[] = templatesQ.data?.items ?? [];

  const sitesQ = useQuery({ queryKey: ["relay-sites"], queryFn: () => relaySitesApi.list() });
  const sites: RelaySite[] = sitesQ.data?.items ?? [];

  const protocolsQ = useQuery({ queryKey: ["protocols"], queryFn: () => protocolsApi.list() });
  const protocols: MainProtocol[] = protocolsQ.data?.items ?? [];

  const [pageMsg, setPageMsg] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [templateFilter, setTemplateFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState("");

  const [fSiteId, setFSiteId] = useState("");
  const [fTplId, setFTplId] = useState(""); // main_template.id
  const [fGroups, setFGroups] = useState("");
  const [fPriority, setFPriority] = useState("100");
  const [fProxy, setFProxy] = useState("");
  const [fModels, setFModels] = useState<string | null>(null);
  const [fModelMapping, setFModelMapping] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<TemplateRelaySite | null>(null);
  const [editErr, setEditErr] = useState<string | null>(null);
  const [createErr, setCreateErr] = useState<string | null>(null);

  const enabledSites = useMemo(() => sites.filter((s) => s.enabled), [sites]);
  const filteredBindings = useMemo(
    () => bindings.filter((b) =>
      (!templateFilter || b.template_id === Number(templateFilter)) &&
      (!siteFilter || b.site_id === Number(siteFilter))),
    [bindings, templateFilter, siteFilter],
  );
  const tplName = (id: number) => templates.find((t) => t.id === id)?.name ?? "";
  const tplBy = (id: number) => templates.find((t) => t.id === id);
  const protoById = (id: number) => protocols.find((p) => p.id === id);
  const siteAdapter = (id: number) => sites.find((s) => s.id === id)?.adapter_type ?? "";
  const siteUrl = (id: number) => sites.find((s) => s.id === id)?.url ?? "";

  const bindMut = useMutation({
    mutationFn: () =>
      templateRelaySitesApi.bind(Number(fTplId), {
        site_id: Number(fSiteId),
        groups: fGroups.trim(),
        priority: Number(fPriority),
        proxy: fProxy.trim(),
        models: fModels,
        model_mapping: fModelMapping,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["relay-bindings"] });
      setFSiteId("");
      setFTplId("");
      setFGroups("");
      setFPriority("100");
      setFProxy("");
      setFModels(null);
      setFModelMapping(null);
      setCreateErr(null);
      setCreateOpen(false);
      setPageMsg("已绑定");
    },
    onError: (e) => setCreateErr(e instanceof ApiError ? e.message : "绑定失败"),
  });

  function submitBind(e: React.FormEvent) {
    e.preventDefault();
    if (!fSiteId || !fTplId) {
      setCreateErr("请选择主模板与站点");
      return;
    }
    const p = Number(fPriority);
    if (!Number.isInteger(p) || p < 0) {
      setCreateErr("优先级必须 ≥ 0(new-api 按 MAX 选路)");
      return;
    }
    setCreateErr(null);
    bindMut.mutate();
  }

  const updateMut = useMutation({
    mutationFn: ({ b, body }: { b: TemplateRelaySite; body: Parameters<typeof templateRelaySitesApi.update>[2] }) =>
      templateRelaySitesApi.update(b.template_id, b.site_id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["relay-bindings"] });
      setPageMsg("已保存");
      setEditTarget(null);
      setEditErr(null);
    },
    onError: (e) => setEditErr(e instanceof ApiError ? e.message : "保存失败"),
  });

  const unlinkMut = useMutation({
    mutationFn: (b: TemplateRelaySite) => templateRelaySitesApi.unbind(b.template_id, b.site_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["relay-bindings"] });
      setPageMsg("已解绑");
    },
    onError: (e) => setPageMsg(e instanceof ApiError ? e.message : "解绑失败"),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-xl font-semibold">中转站绑定</h1>
          <p className="mt-0.5 text-xs text-muted-foreground">
            主模板 × 中转站点 的多对多绑定,承载分组(groups)、优先级(priority)与出站代理(proxy)。
            主键 = 模板ID + 站点ID;一个站点可绑多模板,一个模板可绑多站点。
            proxy 写入 new-api 渠道 setting.proxy(doc/060)。
          </p>
        </div>
        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span>
            <strong className="text-foreground">{filteredBindings.length}</strong> / {bindings.length} 条绑定
          </span>
          {q.isError && <Badge variant="warning">加载失败</Badge>}
          <Button size="sm" className="gap-1.5" onClick={() => { setCreateErr(null); setCreateOpen(true); }}>
            <Plus className="h-3.5 w-3.5" /> 新增绑定
          </Button>
        </div>
      </div>

      {pageMsg && (
        <div className="rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
          {pageMsg}
          <button type="button" className="ml-2 hover:text-foreground" onClick={() => setPageMsg(null)} aria-label="关闭提示">×</button>
        </div>
      )}

      {createOpen && (
        <Modal
          title="新增绑定"
          subtitle="主模板 × 中转站点:选择主模板与站点,填写分组(groups)、优先级(priority)与可选代理地址(proxy)。"
          onClose={() => setCreateOpen(false)}
          defaultSize="lg"
          sizeControls
        >
        {createErr && (
          <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">{createErr}</div>
        )}
        <form className="grid grid-cols-1 gap-3" onSubmit={submitBind}>
          <div className="space-y-1">
            <Label>主模板 <span className="text-destructive">*</span></Label>
            <select className={inpCls} value={fTplId} onChange={(e) => setFTplId(e.target.value)}>
              <option value="">— 选择主模板 —</option>
              {templates.filter((t) => t.enabled).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} (协议 {t.protocol_type})
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>站点 <span className="text-destructive">*</span></Label>
            <select className={inpCls} value={fSiteId} onChange={(e) => setFSiteId(e.target.value)}>
              <option value="">— 选择站点 —</option>
              {enabledSites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} (relay · {s.adapter_type})
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>分组 groups</Label>
            <Input className={inpCls} value={fGroups} onChange={(e) => setFGroups(e.target.value)} placeholder="default,premium" />
          </div>
          <div className="space-y-1">
            <Label>优先级 <span className="text-destructive">*</span></Label>
            <Input className={inpCls} type="number" min="0" step="1" value={fPriority} onChange={(e) => setFPriority(e.target.value)} />
            <p className="text-[11px] text-muted-foreground">MAX 选路,必须 ≥ 0</p>
          </div>
          <div className="space-y-1">
            <Label>代理地址</Label>
            <Input className={inpCls} value={fProxy} onChange={(e) => setFProxy(e.target.value)} placeholder="socks5://192.168.77.11:10808" spellCheck={false} />
            <p className="text-[11px] text-muted-foreground">可选出站代理,空 = 不设;分发/同步时写入 new-api 渠道 setting.proxy</p>
          </div>
          <div className="rounded-lg border bg-muted/20 p-3">
            <InheritedModelConfigEditor
              models={fModels}
              modelMapping={fModelMapping}
              inheritedModels={(sites.find((s) => s.id === Number(fSiteId))?.models ?? templates.find((t) => t.id === Number(fTplId))?.models) || ""}
              inheritedModelMapping={(sites.find((s) => s.id === Number(fSiteId))?.model_mapping ?? templates.find((t) => t.id === Number(fTplId))?.model_mapping) || ""}
              onModelsChange={setFModels}
              onModelMappingChange={setFModelMapping}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>取消</Button>
            <Button type="submit" disabled={bindMut.isPending}>
              {bindMut.isPending ? "保存中…" : "保存绑定"}
            </Button>
          </div>
        </form>
        </Modal>
      )}

      {/* 绑定列表 */}
      <div className="overflow-hidden rounded-xl border bg-card p-3 shadow-sm">
        <div className="mb-2 border-b pb-2.5 text-xs font-medium text-muted-foreground">
          绑定列表 · 按 主模板 → 站点分组
        </div>
        <TableShell
          key={`${templateFilter}|${siteFilter}`}
          mode="client"
          columns={bindingColumns}
          data={filteredBindings}
          emptyText={bindings.length === 0 ? "暂无绑定。选择一个主模板 + 站点开始绑定。" : "没有匹配筛选条件的绑定。"}
          search={{ placeholder: "搜索模板/站点/URL", match: (b) => `${b.template_name} ${b.site_name} ${siteUrl(b.site_id)}` }}
          filters={
            <>
              <select className={`${inpCls} sm:w-56`} value={templateFilter} onChange={(e) => setTemplateFilter(e.target.value)} aria-label="筛选主模板">
                <option value="">全部主模板</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.code} · {t.name}</option>)}
              </select>
              <select className={`${inpCls} sm:w-56`} value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} aria-label="筛选站点">
                <option value="">全部站点</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </>
          }
          rowKey={(b) => `${b.template_id}/${b.site_id}`}
          renderRow={(b) => {
            const t = tplBy(b.template_id);
            return (
              <BindingRow
                b={b}
                templateCode={t?.code ?? ""}
                templateName={b.template_name || t?.name || ""}
                adapterType={siteAdapter(b.site_id)}
                siteUrl={siteUrl(b.site_id)}
                proto={t ? protoById(t.main_protocol_id) : undefined}
                onEdit={() => { setEditTarget(b); setEditErr(null); }}
                onUnlink={() => {
                  if (confirm(`确认解绑「${b.template_name || tplName(b.template_id)} × ${b.site_name}」?`)) {
                    unlinkMut.mutate(b);
                  }
                }}
              />
            );
          }}
        />
      </div>
      {editTarget && (
        <RelayBindingEditModal
          binding={editTarget}
          site={sites.find((s) => s.id === editTarget.site_id)}
          template={tplBy(editTarget.template_id)}
          saving={updateMut.isPending}
          error={editErr}
          onClose={() => { setEditTarget(null); setEditErr(null); }}
          onSave={(body) => updateMut.mutate({ b: editTarget, body })}
        />
      )}
    </div>
  );
}

const bindingColumns: ColumnSpec<TemplateRelaySite>[] = [
  { key: "protocol_type", label: "协议" },
  { key: "template_name", label: "主模板", sortable: true },
  { key: "site_name", label: "站点", sortable: true },
  { key: "groups", label: "分组 groups" },
  { key: "priority", label: "优先级", sortable: true },
  { key: "proxy", label: "代理地址" },
  { key: "models", label: "模型配置" },
  { key: "actions", label: "操作", align: "center" },
];

function BindingRow({
  b,
  templateCode,
  templateName,
  adapterType,
  siteUrl,
  proto,
  onEdit,
  onUnlink,
}: {
  b: TemplateRelaySite;
  templateCode: string;
  templateName: string;
  adapterType: string;
  siteUrl: string;
  proto: MainProtocol | undefined;
  onEdit: () => void;
  onUnlink: () => void;
}) {
  return (
    <tr className="border-b hover:bg-muted/30">
      <td className="px-3 py-2.5">
        <Badge variant="muted">{proto?.code ?? `协议${b.protocol_type}`}</Badge>
        {proto?.name && <div className="mt-0.5 text-[11px] text-muted-foreground">{proto.name}</div>}
      </td>
      <td className="px-3 py-2.5">
        <div className="font-mono text-xs">{templateCode || `#${b.template_id}`}</div>
        {templateName && <div className="text-[11px] text-muted-foreground">{templateName}</div>}
      </td>
      <td className="px-3 py-2.5">
        <div className="font-medium">{b.site_name || `#${b.site_id}`}</div>
        <div className="text-[11px] text-muted-foreground">{adapterType || "relay"}</div>
        {siteUrl ? (
          <a
            href={siteUrl}
            target="_blank"
            rel="noreferrer"
            className="block max-w-56 truncate text-[11px] text-primary hover:underline"
            title={siteUrl}
          >
            {siteUrl}
          </a>
        ) : (
          <div className="text-[11px] text-muted-foreground">—</div>
        )}
      </td>
      <td className="px-3 py-2.5">
        <div className="max-w-56 truncate text-xs" title={b.groups}>{b.groups || "—"}</div>
      </td>
      <td className="px-3 py-2.5">
        <span className="text-xs tabular-nums">{b.priority}</span>
      </td>
      <td className="px-3 py-2.5">
        <div className="max-w-56 truncate text-xs" title={b.proxy || ""}>{b.proxy || "—"}</div>
      </td>
      <td className="px-3 py-2.5">
        <div className="max-w-40 truncate text-xs" title={b.effective_models}>{b.effective_models || "未设置"}</div>
        <div className="text-[11px] text-muted-foreground">{modelSourceLabel(b.models_source)}</div>
      </td>
      <td className="px-3 py-2.5 text-center">
        <div className="flex items-center justify-center gap-1">
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            onClick={onEdit}
            title="编辑绑定"
            aria-label="编辑绑定"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            onClick={onUnlink}
            title="解绑"
            aria-label="解绑"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}
